import React from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent } from '../../../ui/card'
import { Button } from '../../../ui/button'
import { CheckCircle2, AlertTriangle, Calendar, ListChecks } from 'lucide-react'
import { cn } from '@/lib/utils'
import { EventSetupStatus, setupStepHref } from '../eventSetupStatus'

interface FinishStepProps {
  eventId: number | string
  status: EventSetupStatus
  /** Where the user came from, so Finish can send them back there. */
  returnTo?: string
}

export const FinishStep: React.FC<FinishStepProps> = ({
  eventId,
  status,
  returnTo,
}) => {
  const outstanding = status.steps.filter(
    (s) => s.countsTowardProgress && s.status === 'incomplete'
  )
  const allDone = outstanding.length === 0

  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-6 py-10 text-center">
        <div
          className={cn(
            'flex h-16 w-16 items-center justify-center rounded-full',
            allDone ? 'bg-green-100' : 'bg-amber-100'
          )}
        >
          {allDone ? (
            <CheckCircle2 className="h-8 w-8 text-green-600" />
          ) : (
            <AlertTriangle className="h-8 w-8 text-amber-600" />
          )}
        </div>

        <div className="space-y-2">
          <h2 className="text-xl font-semibold">
            {allDone ? 'This event is ready to schedule' : 'Almost there'}
          </h2>
          <p className="max-w-md text-sm text-muted-foreground">
            {allDone
              ? 'Locations, positions, roles and staff are all in place. Head to the schedule to create your first shift and start assigning people.'
              : 'You can start building the schedule, but these steps are still outstanding. You can come back to them at any time from the event page.'}
          </p>
        </div>

        {!allDone && (
          <ul className="w-full max-w-md divide-y rounded-md border text-left">
            {outstanding.map((step) => (
              <li key={step.id}>
                <Link
                  to={setupStepHref(step.id, eventId)}
                  className="flex items-center justify-between px-3 py-2.5 text-sm hover:bg-muted/50"
                >
                  <span className="font-medium">{step.label}</span>
                  <span className="text-xs text-muted-foreground">
                    {step.consequence || step.detail}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}

        <div className="flex flex-wrap justify-center gap-3">
          <Button asChild>
            <Link to={returnTo || `/event/${eventId}/schedule`}>
              <Calendar className="mr-2 h-4 w-4" />
              {returnTo ? 'Back to where you were' : 'Go to the schedule'}
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link to={`/event/${eventId}/config`}>
              <ListChecks className="mr-2 h-4 w-4" />
              Event configuration
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
